import { Add, Delete } from '@mui/icons-material';
import { Alert, FormControlLabel, IconButton, MenuItem, Switch } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { SupportedFunctions, useEventFunctions } from '../../../hooks/useEventFunctions';
import { Challenge } from '../../../interfaces/interfaces';
import { fetchAndSetChallenges } from '../../../reduxToolkit/slices/commonSlice';
import { useDispatch, useSelector } from '../../../reduxToolkit/typedRedux';
import { ChallengeProps, createChallenge, updateChallenge } from '../../../services/services';
import VyButton from '../../common/VyButton';
import VyModal from '../../common/VyModal';
import VySelect from '../../common/VySelect';
import VyTextField from '../../common/VyTextField';
import VyTooltip from '../../common/VyTooltip';

interface ChallengeModalInterface {
  open?: boolean;
  onClose?: () => void;
  currentChallenge?: Challenge;
}

type FormValues = {
  challenge: ChallengeProps;
};

const ChallengeModal = ({ open, onClose, currentChallenge }: ChallengeModalInterface) => {
  const event = useSelector((state) => state.common.event || state.admin.event);

  const { register, handleSubmit, reset, control } = useForm<FormValues>({
    defaultValues: {
      challenge: currentChallenge,
    },
  });
  const { isFunctionEnabled } = useEventFunctions();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'challenge.tasks',
  });

  function getTaskTypes() {
    const taskTypes = [];

    if (event?.modules.contactshare.active) taskTypes.push({ value: 'contact_share', name: t('account:challenges.contact-share') });
    if (isFunctionEnabled(SupportedFunctions.booths, event) && event?.settings.allowBoothLikes)
      taskTypes.push({ value: 'booth_likes', name: t('account:challenges.booth-likes') });
    if (event?.eventType === 'hybrid') taskTypes.push({ value: 'onsite_presentation_scan', name: t('account:challenges.onsite-presentation-scan') });
    if (isFunctionEnabled(SupportedFunctions.videomeet, event))
      taskTypes.push(
        { value: 'networking_join', name: t('account:challenges.networking-join') },
        { value: 'networking_minutes', name: t('account:challenges.networking-minutes') },
        { value: 'meeting_join', name: t('account:challenges.meeting-join') },
        { value: 'meeting_minutes', name: t('account:challenges.meeting-minutes') }
      );
    if (isFunctionEnabled(SupportedFunctions.presentations, event))
      taskTypes.push(
        { value: 'presentation_join', name: t('account:challenges.presentation-join') },
        { value: 'presentation_minutes', name: t('account:challenges.presentation-minutes') }
      );

    return taskTypes;
  }

  useEffect(() => {
    if (!currentChallenge && getTaskTypes().length > 0) {
      append({
        name: '',
        description: '',
        taskType: getTaskTypes()[0].value,
        goal: 0,
      });
    } else if (currentChallenge) {
      reset({ challenge: currentChallenge });
    }
  }, [currentChallenge]);

  async function submit(props: FormValues) {
    if (!event) return;
    try {
      await createChallenge({ eventid: event._id, ...props.challenge });
      dispatch(fetchAndSetChallenges(event._id));
      toast.success(t('toasts:success-new-challenge'));
      onClose?.();
    } catch (err) {
      console.error(err);
    }
  }

  async function update(props: FormValues) {
    if (!event || !currentChallenge?._id) return;
    try {
      await updateChallenge(currentChallenge?._id, props.challenge);
      dispatch(fetchAndSetChallenges(event._id));
      onClose?.();
      toast.success(t('toasts:change-success'));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <VyModal maxWidth="90vw" title={currentChallenge?.name ?? t('account:challenges.new-challenge')} open={open} disableDefaultStyling onClose={onClose}>
      {() =>
        getTaskTypes().length > 0 ? (
          <form onSubmit={handleSubmit(currentChallenge ? update : submit)} className="flex flex-col mb-2 form">
            <div className="flex flex-col justify-between">
              <label>
                <VyTooltip title={t('account:challenges.tooltip-name')}>
                  <span>{t('account:challenges.name')}</span>
                </VyTooltip>
                <Controller
                  control={control}
                  name="challenge.name"
                  defaultValue={currentChallenge?.name}
                  rules={{ required: true }}
                  render={({ field }) => <VyTextField {...field} inputProps={{ maxLength: 35 }} type="text" color="secondary" fullWidth />}
                />
              </label>

              <label>
                <FormControlLabel
                  control={<Switch color="primary" {...register('challenge.isActive')} defaultChecked={currentChallenge?.isActive} />}
                  label={
                    <VyTooltip title={t('account:challenges.tooltip-is-active')}>
                      <span>{t('account:challenges.is-active')}</span>
                    </VyTooltip>
                  }
                />
              </label>

              <div className="flex items-center w-full">
                <VyTooltip title={t('account:challenges.tooltip-tasks')}>
                  <span style={{ height: 'fit-content' }}>{t('account:challenges.tasks')}</span>
                </VyTooltip>
                <IconButton
                  onClick={() => {
                    append({
                      name: '',
                      description: '',
                      taskType: 'contact_share',
                      goal: 0,
                    });
                  }}
                  size="large">
                  <Add color="secondary" />
                </IconButton>
              </div>
              {fields?.map((task, i) => (
                <div key={`task-${i}`} className="flex flex-row items-center justify-center mb-8">
                  <div className="flex flex-col w-full space-y-2">
                    <Controller
                      control={control}
                      name={`challenge.tasks.${[i]}.name`}
                      defaultValue=""
                      rules={{ required: true }}
                      render={({ field }) => <VyTextField {...field} fullWidth label={t('account:challenges.name')} />}
                    />

                    <Controller
                      control={control}
                      name={`challenge.tasks.${[i]}.description`}
                      defaultValue=""
                      render={({ field }) => <VyTextField {...field} fullWidth label={t('account:challenges.description')} multiline rows={2} />}
                    />

                    <Controller
                      control={control}
                      name={`challenge.tasks.${[i]}.goal`}
                      defaultValue=""
                      rules={{ required: true }}
                      render={({ field }) => <VyTextField {...field} fullWidth label={t('account:challenges.goal')} type="number" />}
                    />
                    <div className="flex items-center space-x-2">
                      <span>{t('account:challenges.types')}</span>
                      <Controller
                        control={control}
                        name={`challenge.tasks.${[i]}.taskType`}
                        render={({ field: { onChange, value } }) => (
                          <VySelect id={`task-type-${i}`} value={value} onChange={(e) => onChange(e.target.value)}>
                            {getTaskTypes().map((taskType) => (
                              <MenuItem value={taskType.value} key={taskType.value}>
                                {taskType.name}
                              </MenuItem>
                            ))}
                          </VySelect>
                        )}
                      />
                    </div>
                  </div>
                  <VyTooltip title={fields.length <= 1 ? t('account:challenges.cant-remove-last-task') : ''}>
                    <IconButton
                      disabled={fields.length <= 1}
                      onClick={() => {
                        remove(i);
                      }}
                      size="large">
                      <Delete />
                    </IconButton>
                  </VyTooltip>
                </div>
              ))}
              <VyButton className="w-full" type="submit" isSaveButton>
                {currentChallenge ? t('account:challenges.update') : t('account:challenges.create')}
              </VyButton>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-center">
            <Alert severity="info" className="m-4">
              {t('account:challenges.no-feature-active')}
            </Alert>
          </div>
        )
      }
    </VyModal>
  );
};
export default ChallengeModal;
